/**
 * Review step pane — per `feature-spec.md` §3.4.5.
 *
 *   - One card per dimension with selected labels rendered as chips.
 *     "None apply" or no selections → a single "None apply" chip.
 *   - Per-card "Edit" button jumps directly back to that dimension's step
 *     (closes the prototype gap from §3.4.5 / §6.2).
 *   - One confidence-note `<textarea>` for the whole record.
 *   - Footer: Back to Labels + primary Submit Annotation.
 */
import React from 'react';

import { Badge } from '../../../../../ui/badge';
import { Button } from '../../../../../ui/button';
import { Card } from '../../../../../ui/card';
import { Textarea } from '../../../../../ui/textarea';
import type { TaxonomyDimension } from '../../../types/taxonomy';

interface DimensionStateLike {
  selected: string[];
  noneApply: boolean;
}

interface Props {
  dimensions: TaxonomyDimension[];
  /** Map keyed by dimension id; missing keys mean nothing selected. */
  perDimension: Record<string, DimensionStateLike>;
  confidenceNote: string;
  isSubmitting: boolean;
  onEditDimension: (stepIndex: number) => void;
  onConfidenceNoteChange: (s: string) => void;
  onBack: () => void;
  onSubmit: () => void;
}

const labelNameById = (
  dimension: TaxonomyDimension,
  labelId: string,
): string => {
  for (const cat of dimension.categories) {
    const found = cat.labels.find((l) => l.id === labelId);
    if (found) return found.name;
  }
  return labelId; // fall back to id if not found
};

const ReviewPane: React.FC<Props> = ({
  dimensions,
  perDimension,
  confidenceNote,
  isSubmitting,
  onEditDimension,
  onConfidenceNoteChange,
  onBack,
  onSubmit,
}) => {
  return (
    <div className="space-y-4" data-testid="review-pane">
      <div>
        <h2 className="font-jakarta text-xl font-bold tracking-[-0.02em] text-foreground">
          Review your annotation
        </h2>
        <p className="mt-1 max-w-3xl text-[12px] leading-relaxed text-muted-foreground">
          Check each dimension before submitting. Use the Edit button on a card
          to jump back and revise that dimension's labels.
        </p>
      </div>

      <div className="space-y-3">
        {dimensions.map((dim, idx) => {
          const ds = perDimension[dim.id] ?? { selected: [], noneApply: false };
          const showNoneApply = ds.noneApply || ds.selected.length === 0;
          return (
            <Card
              key={dim.id}
              className="flex items-start justify-between gap-3 p-4"
              data-testid={`review-card-${dim.id}`}
            >
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full bg-primary"
                    aria-hidden="true"
                  />
                  <span className="text-[13px] font-semibold text-foreground">
                    {dim.name}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {showNoneApply ? (
                    <Badge variant="secondary">None apply</Badge>
                  ) : (
                    ds.selected.map((labelId) => (
                      <Badge key={labelId} variant="default">
                        {labelNameById(dim, labelId)}
                      </Badge>
                    ))
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEditDimension(idx)}
                data-testid={`review-edit-${dim.id}`}
              >
                Edit
              </Button>
            </Card>
          );
        })}
      </div>

      <div className="space-y-2">
        <label
          htmlFor="confidence-note"
          className="block text-[13px] font-semibold text-foreground"
        >
          Confidence note <span className="text-muted-foreground">(optional)</span>
        </label>
        <Textarea
          id="confidence-note"
          value={confidenceNote}
          onChange={(e) => onConfidenceNoteChange(e.target.value)}
          rows={3}
          placeholder="Any notes the second annotator or adjudicator should see…"
          data-testid="confidence-note-textarea"
        />
      </div>

      <div className="flex items-center justify-between border-t border-border pt-4">
        <Button variant="ghost" onClick={onBack} disabled={isSubmitting}>
          Back to labels
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isSubmitting}
          data-testid="submit-annotation"
        >
          {isSubmitting ? 'Submitting…' : 'Submit annotation'}
        </Button>
      </div>
    </div>
  );
};

export default ReviewPane;
